<template>
  <div class="v-paginator__wrapper" :class="{ [classType]: true }">
    <div
      v-if="hasPrev"
      class="v-paginator__page arrows"
      @click="paginate(currentPage - 1)"
    >
      <i class="far fa-chevron-left"></i>
    </div>
    <div v-if="showFirst" class="v-paginator__page" @click="paginate(1)">
      <span>1</span>
    </div>
    <div v-if="showFirst" class="v-paginator__range-dots">
      <img :src="threeDots" alt="three-dots" />
    </div>
    <div
      v-for="(page, idx) in pagesToDisplay"
      class="v-paginator__page"
      :key="idx"
      :class="{ ['active']: page === currentPage }"
      @click="paginate(page)"
    >
      <span>{{ page }} </span>
    </div>
    <div v-if="showLast" class="v-paginator__range-dots">
      <img :src="threeDots" alt="three-dots" />
    </div>
    <div
      class="v-paginator__page"
      @click="paginate(pagesCount)"
      :class="{ ['active']: pagesCount === currentPage }"
      v-if="pagesCount !== 0"
    >
      <span>{{ pagesCount }} </span>
    </div>
    <div
      v-if="hasNext"
      class="v-paginator__page arrows"
      @click="paginate(currentPage + 1)"
    >
      <i class="far fa-chevron-right"></i>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import range from "lodash/range";

import withToasted from "@/mixins/withToasted";
import threeDots from "@/assets/images/three-dots.svg";

export default Vue.component("VPagination", {
  props: {
    // specify number of items
    totalItems: VueTypes.number,

    // specify total pages directly
    totalPages: VueTypes.number,
    itemsPerPage: VueTypes.number.def(10),
    currentPage: VueTypes.number.def(1),

    // count of pages number displayed simultaneously
    // 1, 2, 3, 4, 5, 6, 7, 8, 9, 10 ... last
    pagesRange: VueTypes.number.def(6),
    classType: VueTypes.oneOf(["primary", "secondary", "tertiary"]).def(
      "primary"
    )
  },

  mixins: [withToasted],

  data() {
    return {
      threeDots
    };
  },

  computed: {
    pagesCount() {
      return this.totalPages
        ? this.totalPages
        : Math.ceil(this.totalItems / this.itemsPerPage);
    },

    showFirst() {
      return this.currentPage - this.halfDisplayedRange > 2;
    },

    showLast() {
      return this.currentPage + this.halfDisplayedRange < this.pagesCount;
    },

    halfDisplayedRange() {
      return Math.ceil(this.pagesRange / 2);
    },

    hasNext() {
      return this.currentPage + 1 <= this.pagesCount;
    },

    hasPrev() {
      return this.currentPage - 1 > 0;
    },

    pagesToDisplay() {
      let startPage = this.currentPage - this.halfDisplayedRange;
      let endPage = this.currentPage + this.halfDisplayedRange;

      if (startPage <= 0) {
        startPage = 1;
      }

      if (endPage >= this.pagesCount) {
        endPage = this.pagesCount;
      }

      if (startPage > 1) {
        startPage = startPage + 1;
      }

      return range(startPage, endPage);
    }
  },

  methods: {
    paginate(page) {
      // prevent click on the same page
      if (page === this.currentPage) {
        return;
      }

      this.pageFromInput = page;
      this.$emit("change", page);
    },

    goToPage() {
      if (this.pageFromInput > this.pagesCount) {
        this.toast(`Page #${this.pageFromInput} doesn't exist`, "error", {
          duration: 4000,
          position: "bottom-right"
        });

        return;
      }

      this.paginate(Number(this.pageFromInput));
    }
  }
});
</script>

<style lang="scss" scoped>
$ns: "v-paginator";

.#{$ns}__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  height: 32px;

  input[type="number"] {
    min-width: 10px;
    font-size: 15px;
    color: black;
    background: transparent;
    border: none;
    outline: none;
    -moz-appearance: textfield;
    width: 100%;
    text-align: center;
    padding: 0;
    cursor: pointer;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}

.#{$ns}__range-dots {
  border-radius: 5px;
  margin-right: 12px;
  color: #adadad;
}

.#{$ns}__page {
  border-radius: 5px;
  margin-right: 12px;
  min-width: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $secondary-text-color;
  border: 1px solid transparent;
  background-color: #ebedf0;

  &.active {
    cursor: pointer;
  }

  &:last-child {
    margin-right: 0;
  }
}

// COLORS VARIETY
.#{$ns}__wrapper {
  &.primary {
    .#{$ns}__page {
      &:hover,
      &.active {
        color: $white;
        background-color: $primary-color;
      }
    }
  }

  &.secondary {
    .#{$ns}__page {
      &:hover,
      &.active {
        border-color: $secondary-color;
      }
    }
  }

  &.tertiary {
    .#{$ns}__page {
      &:hover,
      &.active {
        border-color: $tertiary-color;
      }
    }
  }

  &.primary,
  &.secondary {
    .arrows {
      display: flex;
      color: $primary-grey;
      background-color: transparent;
      margin: 0 3px;

      &:hover {
        background-color: transparent;
        color: $primary-color;
      }

      i {
        font-size: 15px;
      }

      span {
        height: 18px;

        &:first-child {
          margin-right: 5px;
        }

        &:last-child {
          margin-left: 5px;
        }
      }

      &.disabled {
        pointer-events: none;
        color: $disabled-color;

        &:hover {
          color: currentColor;
          background: transparent;
        }
      }
    }
  }
}

// MEDIA
@media screen and (max-width: 991px) {
  .#{$ns}__page.arrows {
    span {
      display: none;
    }
  }
}

@media screen and (max-width: 468px) {
  .#{$ns}__wrapper {
    flex-wrap: wrap;
  }
}
</style>
